import type { Nullable } from 'corgi-types';

import { LINE_NO_SEPARATOR } from '../../../../contact-form/utils/products';
import {
  MAX_CHARS_EMAIL_ADDRESS,
  MAX_CHARS_NAME,
  REGEX_COMPANY,
  REGEX_EMAIL_ADDRESS,
  REGEX_LETTERS_DOTS_AND_COMMAS_ONE_LETTER,
  REGEX_MARKETPLACE_ORDER_NUMBER,
  REGEX_ORDER_NUMBER,
  REGEX_VALID_VAT_NUMBER,
  REGEX_VALID_VAT_TAX_NUMBER,
} from '../../../constants/form';
import type { LengthValidatorType } from '../../../enums/ValidatorType';
import type { FieldValidationMap, FieldValue, FormData, ValidationMap } from '../../../types/form';

interface ValidateFieldOptions<FieldName extends string> {
  value: string;
  formData: FormData<FieldName>;
  validator: Nullable<FieldValidationMap<FieldName>>;
}

interface IsFormValidOptions<FieldName extends string> {
  values: Map<FieldName, FieldValue>;
  validationMap: ValidationMap<FieldName>;
}

export const isValidatorOptional = <FieldName extends string>(
  validatorMap?: Nullable<FieldValidationMap<FieldName>>,
  value?: string
) => !validatorMap?.length && !value;

export const isLengthValid = <FieldName extends string>(
  validatorMap: Nullable<FieldValidationMap<FieldName>>,
  value: string
) => !validatorMap?.length || (!!value && value.length >= validatorMap.length);

const isValueValid = <FieldName extends string>(
  validatorMap: Nullable<FieldValidationMap<FieldName>>,
  value: string,
  formData: FormData<FieldName>
) => {
  if (!validatorMap?.validate) {
    return true;
  }

  return Object.values(validatorMap.validate).every(validateFn => validateFn(value, formData));
};

export const validateField = <FieldName extends string>({
  value,
  formData,
  validator,
}: ValidateFieldOptions<FieldName>) =>
  isValidatorOptional(validator, value) ||
  (!!value && isLengthValid(validator, value) && isValueValid(validator, value, formData));

export const isValidEmailAddress = (value: string) =>
  value.length <= MAX_CHARS_EMAIL_ADDRESS && REGEX_EMAIL_ADDRESS.test(value);

export const isValidName = (value: string) =>
  value.length <= MAX_CHARS_NAME && REGEX_LETTERS_DOTS_AND_COMMAS_ONE_LETTER.test(value);

export const isOnlyDigits = (value: string) => /^[0-9]+$/.test(value);

export const isValidProductNumber = (value: string) => {
  if (!value) return false;

  return value.split(LINE_NO_SEPARATOR).every(isOnlyDigits);
};

export const isValidVatNumber = (value: string) => REGEX_VALID_VAT_NUMBER.test(value);

export const isValidVatTaxNumber = (value: string) => REGEX_VALID_VAT_TAX_NUMBER.test(value);

export const isValidOrderNumber = (value: string) => REGEX_ORDER_NUMBER.test(value);

export const isValidMarketplaceOrderNumber = (value?: string) =>
  value ? REGEX_MARKETPLACE_ORDER_NUMBER.test(value) : false;

export const isValidCompany = (value: string) => REGEX_COMPANY.test(value);

export const isFormValid = <FieldName extends string>({
  values,
  validationMap,
}: IsFormValidOptions<FieldName>) => {
  const validationMapEntries = validationMap.entries();
  for (const [fieldName, validator] of validationMapEntries) {
    if (
      !validateField({
        validator,
        value: values.get(fieldName) ?? '',
        formData: values,
      })
    ) {
      return false;
    }
  }

  return true;
};

export const isRequiredFormGroup = (validationMap?: { [LengthValidatorType.Length]?: number }) =>
  (validationMap?.length ?? 0) > 0;
